import React from 'react';

import type { Core } from '@pdftron/webviewer';
import type { AlineClientEditor } from 'src/layout-engine';

import { createPublishedTabScopedRegistry } from './tab-scoped-registry';

export interface EditorRuntime {
  documentId: UUID;
  documentViewer: Core.DocumentViewer | null;
  editor: AlineClientEditor;
  editorLoading: boolean;
  tabId: UUID;
}

/**
 * Editor runtimes are owned by tab id, not by whichever editor subtree happens
 * to be active. This lets global UI, like the right drawer, ask for "the runtime
 * for tab X" without relying on portals or a single mutable active editor.
 *
 * Keep high-frequency UI state like selection/focus in editor-ui-state-registry
 * so runtime subscribers do not re-render on every cursor or focus update.
 */
const editorRuntimeRegistry = createPublishedTabScopedRegistry<EditorRuntime>({
  useTabIdErrorMessage: 'useEditor must be used within an editor runtime.',
});
const PublishedEditorRuntimeScope = editorRuntimeRegistry.Scope;
const PublishedEditorRuntimeProvider = editorRuntimeRegistry.Provider;
const EditorRuntimeScopeContext = React.createContext<EditorRuntime | null | undefined>(undefined);

/**
 * Hooks still read from React context so existing callsites stay scoped and
 * simple. The context value can come either from the local editor subtree
 * (`EditorRuntimeProvider`) or from a global consumer scoped to a tab
 * (`EditorRuntimeScope`).
 */
export function EditorRuntimeProvider({
  children,
  runtime,
}: {
  children: React.ReactNode;
  runtime: EditorRuntime;
}): JSX.Element {
  return (
    <PublishedEditorRuntimeProvider state={runtime} tabId={runtime.tabId}>
      {children}
    </PublishedEditorRuntimeProvider>
  );
}

export function EditorRuntimeScope({
  children,
  documentId,
  tabId,
}: {
  children: React.ReactNode;
  tabId: UUID;
  documentId?: UUID | null;
}): JSX.Element {
  const runtime = useMaybeEditorRuntimeForTabId(tabId);
  const lastRuntimeRef = React.useRef<EditorRuntime | null>(null);
  const liveRuntime = getIsExpectedRuntime(runtime, documentId) ? runtime : null;

  if (liveRuntime) lastRuntimeRef.current = liveRuntime;

  const cachedRuntime = getIsExpectedRuntime(lastRuntimeRef.current, documentId)
    ? lastRuntimeRef.current
    : null;
  const scopedRuntime = liveRuntime ?? cachedRuntime;

  return (
    <PublishedEditorRuntimeScope tabId={tabId}>
      <EditorRuntimeScopeContext.Provider value={scopedRuntime}>
        {children}
      </EditorRuntimeScopeContext.Provider>
    </PublishedEditorRuntimeScope>
  );
}

export function useMaybeEditorRuntime(): EditorRuntime | null {
  const scopedRuntime = React.useContext(EditorRuntimeScopeContext);
  const registryRuntime = editorRuntimeRegistry.useMaybeState();

  return scopedRuntime !== undefined ? scopedRuntime : registryRuntime;
}

/**
 * Reserved for infrastructure that sits above/outside the tab and editor tree,
 * such as global drag/drop. Normal UI should prefer EditorRuntimeScope or the
 * scoped hooks so runtime ownership remains visible in the render tree.
 */
export function useMaybeEditorRuntimeForTabId(tabId: UUID | null): EditorRuntime | null {
  return editorRuntimeRegistry.useSelectorForTabId(tabId, state => state);
}

export function useRequiredEditorRuntime(): EditorRuntime {
  const runtime = useMaybeEditorRuntime();
  if (!runtime) throw new Error('useEditor must be used within an editor runtime.');
  return runtime;
}

function getIsExpectedRuntime(
  runtime: EditorRuntime | null,
  documentId: UUID | null | undefined
): runtime is EditorRuntime {
  if (!runtime || documentId === null) return false;
  return documentId === undefined || runtime.documentId === documentId;
}
